import { PlacementErrorType } from '@frontend/sports/types/betslip';
import { createSelector } from '@ngrx/store';

import { BetslipType } from '../../core/betslip-type';
import { BetslipUnknownPick } from '../../core/picks/betslip-unknown-pick';
import { getLegsCount } from '../../core/utils';
import { comboBetPicksSelector, comboBetSelectedPicksSelector } from '../combo-bet/selectors';
import { betslipPicksListSelector, selectBetslipFlattenedPicksList } from '../picks/selectors';
import { singleBetSelectedPicksSelector } from '../single-bet/selectors';
import { betslipCurrentTypeSelector } from '../types/base/selectors';
import { MinSelectionsBetbuilderError } from '../validation/errors/general/min-selections-betbuilder-error';
import { PickLockedPreCheckError } from '../validation/errors/pre-check/pick-locked-pre-check-error';
import { selectBetslipErrors, selectCurrentPicksErrors, selectPickErrorsState } from '../validation/selectors';
import { isPickStatusChangeError } from '../validation/services/utils/betslip-errors-utils';

export const selectedComboPicksSelector = createSelector(betslipPicksListSelector, comboBetSelectedPicksSelector, (pickList, selectedPicks) =>
    pickList.filter(({ id }) => !!selectedPicks[id.toString()]),
);

//include unselected picks
export const comboPickListCountSelector = createSelector(betslipPicksListSelector, comboBetPicksSelector, (pickList, comboBetPicks) => {
    const comboBetPick = pickList.filter(({ id }) => !!comboBetPicks[id.toString()]);

    return getLegsCount(comboBetPick);
});

export const selectSelectedSinglePicks = createSelector(selectBetslipFlattenedPicksList, singleBetSelectedPicksSelector, (pickList, selectedPicks) =>
    pickList.filter(({ id }) => !!selectedPicks[id.toString()]),
);

export const hasPickLockedPreCheckErrorSelector = createSelector(selectedComboPicksSelector, selectPickErrorsState, (selectedPicks, pickErrors) =>
    selectedPicks.some((pick) => {
        const errors = Object.values(pickErrors).flatMap((e) => e[pick.id.toString()] ?? []);

        return errors.some((e) => e instanceof PickLockedPreCheckError);
    }),
);

export const hasComboPreventionErrorSelector = createSelector(selectedComboPicksSelector, selectCurrentPicksErrors, (selectedPicks, pickErrors) =>
    selectedPicks.some((pick) => {
        const errors = pickErrors[pick.id.toString()] ?? [];

        return errors.some((e) => e.type === PlacementErrorType.ComboPrevention);
    }),
);

export const pickStatusChangeErrorSelector = createSelector(selectedComboPicksSelector, selectPickErrorsState, (selectedPicks, pickErrors) =>
    selectedPicks.some((pick) => {
        const errors = Object.values(pickErrors).flatMap((e) => e[pick.id.toString()] ?? []);

        return errors.some((e) => isPickStatusChangeError(e));
    }),
);

export const singlePickCountSelector = createSelector(selectSelectedSinglePicks, (selectedPicks) => {
    return getLegsCount(selectedPicks);
});

export const comboPickCountSelector = createSelector(selectedComboPicksSelector, (selectedPicks) => {
    return getLegsCount(selectedPicks);
});

export const hasMinSelectionsBetbuilderError = createSelector(selectBetslipErrors, (betslipError) =>
    betslipError.some((e) => e instanceof MinSelectionsBetbuilderError),
);

export const selectedPicksByCurrentTypeSelector = createSelector(
    betslipCurrentTypeSelector,
    selectedComboPicksSelector,
    selectSelectedSinglePicks,
    (currentType, selectedComboPicks, selectedSinglePicks) => {
        return currentType === BetslipType.Combo
            ? selectedComboPicks.filter((pick) => !BetslipUnknownPick.isPick(pick))
            : selectedSinglePicks.filter((pick) => !BetslipUnknownPick.isPick(pick));
    },
);
